/**
 * SideTooltip: a right-hand tooltip for icon-only sidebar rows (collapsed sidebar). Shows on hover and on keyboard
 * focus, portaled so the nav's scroll container never clips it. The wrapped control keeps its own aria-label; the
 * tooltip is visual only (aria-hidden would hide it from tests, so it carries role="tooltip" and is not focusable).
 *
 *   <SideTooltip label="Logs" enabled={collapsed}><Link aria-label="Logs" ...>{icon}</Link></SideTooltip>
 */
import { ReactNode, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function SideTooltip({ label, enabled, children }: { label: string; enabled: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = useCallback(() => {
    const el = ref.current?.firstElementChild ?? ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + 8 });
  }, []);
  const hide = useCallback(() => setPos(null), []);

  if (!enabled) return <>{children}</>;
  return (
    <div ref={ref} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {pos &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: pos.top, left: pos.left }}
            className="pointer-events-none fixed z-[10001] -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-popover dark:bg-gray-100 dark:text-gray-900"
          >
            {label}
          </div>,
          document.body
        )}
    </div>
  );
}
